import React, { useState, useEffect, useRef } from 'react'
import { ConnectWebSocketForOverlay, SetSocketMessageHandler, ClearSocketMessageHandler, DisconnectWebSocket } from './../../utilities/BL/Zones'
import { GetSoccerConfig } from './../../utilities/BL/Soccer'
import { GetDanceConfig } from './../../utilities/BL/Dance'
import { GetBongoConfig } from './../../utilities/BL/Bongo'
import { GetKissConfig } from './../../utilities/BL/Kiss'
import { GetScoreState } from './../../utilities/BL/Score'

import { Soccer as SoccerOverlay } from './Overlays/Soccer'
import { Dance as DanceOverlay } from './Overlays/Dance'
import { Bongo as BongoOverlay } from './Overlays/Bongo'
import { Kiss as KissOverlay } from './Overlays/Kiss'
import { Lineup as LineupOverlay } from './Overlays/Lineup'
import { Score as ScoreOverlay } from './Overlays/Score'
import { Cards as CardsOverlay } from './Overlays/Cards'

const NORMAL_DANCERS = Array.from({ length: 8 }, (_, i) => `normal/${i + 1}.gif`);
const FAST_DANCERS = Array.from({ length: 14 }, (_, i) => `fast/${i + 1}.gif`);

/**
 * COMPONENTE: SOCCER OVERLAY GENERAL (UNIFICADO EN /abcdef/overlays/soccer)
 * -------------------------------------------------------------------------
 * Consólida Asistencia, Dance Cam, Bongo Cam y Kiss Cam en un único OBS Viewport.
 * Conexión WebSocket SignalR sobre el grupo "tombola" escuchando los 4 tipos.
 * -------------------------------------------------------------------------
 */
const Soccer = () => {
    // --- 1. CONFIGS ---
    const [config, setConfig] = useState({
        visible: false, titleText: 'ASISTENCIA TOTAL', numberText: '333', thanksText: '¡GRACIAS!',
        scale: 1.0, titleSize: 0.9, numberSize: 14.0, thanksSize: 2.2, offsetX: 0, offsetY: -120,
        logoDataUrl: null, logoScale: 1.0, logoOffsetX: 0, logoOffsetY: 0, showLogoSweep: true
    });

    const [danceConfig, setDanceConfig] = useState({
        visible: false, titleText: 'BÁILELE', frameStyle: 'neon-classic', bpm: 120, scale: 1.0,
        titleSize: 8.0, offsetX: 0, offsetY: 0, frameZoom: 94, enableDancers: true
    });

    const [bongoConfig, setBongoConfig] = useState({
        enabled: false, visible: false, bongoSkin: 'classic-wood', hitEffect: 'shockwave',
        hitColor: '#00ffff', scale: 1.0, offsetX: 0, offsetY: 0, bpm: 140, audioReact: false
    });

    const [kissConfig, setKissConfig] = useState({
        visible: false, titleText: 'KISS CAM', failText: '¡BUUUUU!', frameStyle: 'classic-red',
        frameColor: '#ff0033', borderWidth: 6, frameZoom: 92, lowerThirdBgColor: '#000000',
        lowerThirdBgOpacity: 0.6, screenFilter: 'none', floatingShape: 'hearts',
        shapeOpacity: 0.3, shapeGlow: 15, logoDataUrl: null, logoCorner: 'top-left',
        logoScale: 1.0, scale: 1.0, titleSize: 3.5, offsetX: 0, offsetY: 0
    });

    const [connectionStatus, setConnectionStatus] = useState('idle');

    // --- ESTADOS DE ALINEACIÓN, MARCADOR Y TARJETAS ---
    const [lineupData, setLineupData] = useState(null);
    const [scoreData, setScoreData] = useState(null);
    const [cardsData, setCardsData] = useState(null);
    const [isLineupOverlayVisible, setIsLineupOverlayVisible] = useState(false);
    const [isScoreOverlayVisible, setIsScoreOverlayVisible] = useState(false);

    // --- 2. ESTADOS DINÁMICOS DANCE CAM ---
    const [activeDancers, setActiveDancers] = useState([]);
    const [fiestaMode, setFiestaMode] = useState(false);
    const [explodeTrigger, setExplodeTrigger] = useState(0);
    const dancerTimeoutRef = useRef(null);

    // --- 3. ESTADOS DINÁMICOS BONGO CAM ---
    const [leftHit, setLeftHit] = useState(false);
    const [rightHit, setRightHit] = useState(false);
    const [intensityLevel, setIntensityLevel] = useState(0);
    const [comboCount, setComboCount] = useState(0);
    const [shaking, setShaking] = useState(false);
    const comboTimeoutRef = useRef(null);
    const autoIntervalRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const streamRef = useRef(null);
    const audioIntervalRef = useRef(null);
    const toggleHitRef = useRef(true);

    // --- 4. ESTADOS DINÁMICOS KISS CAM ---
    const [tensionActive, setTensionActive] = useState(false);
    const [heartTensionActive, setHeartTensionActive] = useState(false);
    const [successActive, setSuccessActive] = useState(false);
    const [failActive, setFailActive] = useState(false);
    const [destelloActive, setDestelloActive] = useState(false);

    // Configuración inicial de pantalla completa OBS
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
            rootElement.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (rootElement) {
                rootElement.style.removeProperty('background-color');
                rootElement.style.removeProperty('overflow');
            }
        };
    }, []);

    // Carga inicial y registro de WebSocket único
    useEffect(() => {
        const loadConfigsAndSockets = async () => {
            // Cargar configs HTTP iniciales
            const rSoccer = await GetSoccerConfig();
            if (rSoccer.success && rSoccer.data) setConfig(rSoccer.data);

            const rDance = await GetDanceConfig();
            if (rDance.success && rDance.data) setDanceConfig(rDance.data);

            const rBongo = await GetBongoConfig();
            if (rBongo.success && rBongo.data) setBongoConfig(rBongo.data);

            const rKiss = await GetKissConfig();
            if (rKiss.success && rKiss.data) setKissConfig(rKiss.data);

            const rScore = await GetScoreState();
            if (rScore.success && rScore.data) {
                setScoreData(rScore);
                setIsScoreOverlayVisible(rScore.data.isVisible);
            }

            // Conectar al Hub bajo el identificador único de fútbol/tombola
            await ConnectWebSocketForOverlay(
                'tombola',
                (status) => setConnectionStatus(status),
                (msg) => console.log('[Soccer Unified Overlay] WS status:', msg)
            );

            SetSocketMessageHandler(handleIncomingMessage);
        };

        const handleIncomingMessage = (message) => {
            if (!message) return;
            const typeKey = message.type || message.Type;
            if (!typeKey) return;

            const msgType = typeKey.toLowerCase();
            const payload = message.data || message.Data;

            // --- SOCCER/ASISTENCIA ---
            if (msgType === 'soccer') {
                if (payload.action === 'config') setConfig(payload.config);
                else if (payload.action === 'show') setConfig(prev => ({ ...prev, visible: true }));
                else if (payload.action === 'hide') setConfig(prev => ({ ...prev, visible: false }));
            }
            // --- DANCE CAM ---
            else if (msgType === 'dance') {
                if (payload.action === 'config') setDanceConfig(payload.config);
                else if (payload.action === 'show') setDanceConfig(prev => ({ ...prev, visible: true }));
                else if (payload.action === 'hide') setDanceConfig(prev => ({ ...prev, visible: false }));
                else if (payload.action === 'explode') setExplodeTrigger(prev => prev + 1);
                else if (payload.action === 'fiesta') {
                    const stateVal = payload.state === true || payload.state === 'true' || payload.State === true || payload.State === 'true';
                    setFiestaMode(stateVal);
                }
                else if (payload.action === 'dancers') {
                    const stateVal = payload.state === true || payload.state === 'true' || payload.State === true || payload.State === 'true';
                    const isFastVal = payload.isFast === true || payload.isFast === 'true' || payload.IsFast === true || payload.IsFast === 'true';
                    triggerDancers(isFastVal, stateVal);
                }
            }
            // --- BONGO CAM ---
            else if (msgType === 'bongo') {
                if (payload.action === 'config') setBongoConfig(payload.config);
                else if (payload.action === 'show') setBongoConfig(prev => ({ ...prev, visible: true }));
                else if (payload.action === 'hide') setBongoConfig(prev => ({ ...prev, visible: false }));
                else if (payload.action === 'hit') triggerBongoHit(payload.side === 'left');
                else if (payload.action === 'mode') setIntensityLevel(payload.level);
            }
            // --- KISS CAM ---
            else if (msgType === 'kiss') {
                if (payload.action === 'config') setKissConfig(payload.config);
                else if (payload.action === 'show') setKissConfig(prev => ({ ...prev, visible: true }));
                else if (payload.action === 'hide') setKissConfig(prev => ({ ...prev, visible: false }));
                else if (payload.action === 'success') {
                    setSuccessActive(payload.state);
                    if (payload.state) {
                        setFailActive(false);
                        setDestelloActive(false);
                        setTensionActive(false);
                        setHeartTensionActive(false);
                    }
                }
                else if (payload.action === 'fail') {
                    setFailActive(payload.state);
                    if (payload.state) {
                        setSuccessActive(false);
                        setDestelloActive(false);
                        setTensionActive(false);
                        setHeartTensionActive(false);
                    }
                }
                else if (payload.action === 'destello') {
                    setDestelloActive(payload.state);
                    if (payload.state) {
                        setSuccessActive(false);
                        setFailActive(false);
                        setTensionActive(false);
                        setHeartTensionActive(false);
                    }
                }
                else if (payload.action === 'tension') {
                    setTensionActive(payload.state);
                    if (payload.state) {
                        setHeartTensionActive(false);
                        setSuccessActive(false);
                        setFailActive(false);
                        setDestelloActive(false);
                    }
                } else if (payload.action === 'hearttension') {
                    setHeartTensionActive(payload.state);
                    if (payload.state) {
                        setTensionActive(false);
                        setSuccessActive(false);
                        setFailActive(false);
                        setDestelloActive(false);
                    }
                } else if (payload.action === 'reset') {
                    resetKissOverlay();
                }
            }
            // --- LINEUP / ALINEACIÓN ---
            else if (msgType.startsWith('lineup')) {
                setLineupData(message);
                if (payload && payload.action) {
                    if (payload.action === 'show_board') setIsLineupOverlayVisible(true);
                    else if (payload.action === 'hide_board') setIsLineupOverlayVisible(false);
                }
            }
            // --- SCORE / MARCADOR ---
            else if (msgType.startsWith('score')) {
                setScoreData(message);
                if (payload) {
                    if (payload.type === 'visibility_show') setIsScoreOverlayVisible(true);
                    else if (payload.type === 'visibility_hide') setIsScoreOverlayVisible(false);
                    else if (payload.isVisible !== undefined) setIsScoreOverlayVisible(payload.isVisible);
                }
            }
            // --- CARDS / DISCIPLINA ---
            else if (msgType.startsWith('cards')) {
                setCardsData(message);
            }
        };

        loadConfigsAndSockets();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
            stopAudioReact();
            stopAutoPlay();
        };
    }, []);

    // --- EVENTOS DANCE CAM (DANCERS) ---
    const triggerDancers = (isFast, state = false) => {
        if (dancerTimeoutRef.current) clearTimeout(dancerTimeoutRef.current);
        if (!state) {
            const dancersEls = document.querySelectorAll('.dancers-character-gif');
            dancersEls.forEach(el => el.classList.add('dancer-exit'));
            dancerTimeoutRef.current = setTimeout(() => {
                setActiveDancers([]);
            }, 500);
        } else {
            const bank = isFast ? FAST_DANCERS : NORMAL_DANCERS;
            const idx1 = Math.floor(Math.random() * bank.length);
            let idx2 = Math.floor(Math.random() * bank.length);
            while (idx1 === idx2 && bank.length > 1) idx2 = Math.floor(Math.random() * bank.length);

            setActiveDancers([bank[idx1], bank[idx2]]);
        }
    };

    // --- EVENTOS BONGO CAM (AUTO BPM & MIC) ---
    useEffect(() => {
        if (!bongoConfig.enabled || !bongoConfig.visible) {
            stopAutoPlay();
            stopAudioReact();
            return;
        }
        if (bongoConfig.audioReact) {
            stopAutoPlay();
            startAudioReact();
        } else {
            stopAudioReact();
            // startAutoPlay(); // Comentado para mantener los bongos fijos
        }
        return () => {
            stopAutoPlay();
            stopAudioReact();
        };
    }, [bongoConfig.audioReact, bongoConfig.enabled, bongoConfig.visible, bongoConfig.bpm]);

    const triggerBongoHit = (isLeft, isManual = false) => {
        if (isLeft) {
            setLeftHit(true);
            setTimeout(() => setLeftHit(false), 80);
        } else {
            setRightHit(true);
            setTimeout(() => setRightHit(false), 80);
        }
        if (intensityLevel >= 3) {
            setShaking(true);
            setTimeout(() => setShaking(false), 120);
        }
        if (intensityLevel === 4 && isManual) {
            setComboCount(prev => prev + 1);
            if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
            comboTimeoutRef.current = setTimeout(() => setComboCount(0), 1500);
        }
    };

    const startAutoPlay = () => {
        stopAutoPlay();
        const intervalMs = 60000 / (bongoConfig.bpm || 140);
        autoIntervalRef.current = setInterval(() => {
            triggerBongoHit(toggleHitRef.current, false);
            toggleHitRef.current = !toggleHitRef.current;
        }, intervalMs);
    };

    const stopAutoPlay = () => {
        if (autoIntervalRef.current) {
            clearInterval(autoIntervalRef.current);
            autoIntervalRef.current = null;
        }
    };

    const startAudioReact = async () => {
        stopAudioReact();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;

            const microphone = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            analyserRef.current = analyser;

            microphone.connect(analyser);

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            let lastHitTime = 0;

            const processAudio = () => {
                if (!analyserRef.current) return;
                analyserRef.current.getByteFrequencyData(dataArray);

                let bassSum = 0;
                for (let i = 0; i < 10; i++) bassSum += dataArray[i];
                let bassAvg = bassSum / 10;

                const now = Date.now();
                if (bassAvg > 200 && (now - lastHitTime > 150)) {
                    triggerBongoHit(toggleHitRef.current, false);
                    toggleHitRef.current = !toggleHitRef.current;
                    lastHitTime = now;
                }
                audioIntervalRef.current = requestAnimationFrame(processAudio);
            };
            processAudio();
        } catch (err) {
            console.error('Bongo Mic audio-react error:', err);
        }
    };

    const stopAudioReact = () => {
        if (audioIntervalRef.current) cancelAnimationFrame(audioIntervalRef.current);
        if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
        if (audioContextRef.current) audioContextRef.current.close();
        audioIntervalRef.current = null;
        streamRef.current = null;
        audioContextRef.current = null;
        analyserRef.current = null;
    };

    // --- EVENTOS KISS CAM ---
    const resetKissOverlay = () => {
        setTensionActive(false);
        setHeartTensionActive(false);
        setSuccessActive(false);
        setFailActive(false);
        setDestelloActive(false);
    };

    // --- 5. ATASJOS TECLADO CONTEXTUAL EN OBS ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Atajos Contextuales de Kiss Cam
            if (kissConfig.visible) {
                if (e.key === '1') {
                    setDestelloActive(prev => !prev);
                    setSuccessActive(false);
                    setFailActive(false);
                    setTensionActive(false);
                    setHeartTensionActive(false);
                }
                else if (e.key === '2') {
                    setSuccessActive(prev => !prev);
                    setDestelloActive(false);
                    setFailActive(false);
                    setTensionActive(false);
                    setHeartTensionActive(false);
                }
                else if (e.key === '3') {
                    setFailActive(prev => !prev);
                    setDestelloActive(false);
                    setSuccessActive(false);
                    setTensionActive(false);
                    setHeartTensionActive(false);
                }
                else if (e.key === '4') {
                    setHeartTensionActive(prev => !prev);
                    setDestelloActive(false);
                    setSuccessActive(false);
                    setFailActive(false);
                    setTensionActive(false);
                } 
                else if (e.key === '5') {
                    setTensionActive(prev => !prev);
                    setDestelloActive(false);
                    setSuccessActive(false);
                    setFailActive(false);
                    setHeartTensionActive(false);
                }
                else if (e.key === '0') resetKissOverlay();
                else if (e.code === 'Space') {
                    e.preventDefault();
                    setKissConfig(prev => ({ ...prev, visible: !prev.visible }));
                }
            }
            // Atajos Contextuales de Bongo Cam
            else if (bongoConfig.enabled && bongoConfig.visible) {
                if (e.key === '1') triggerBongoHit(true, true);
                else if (e.key === '2') triggerBongoHit(false, true);
                else if (e.key === '3') setIntensityLevel(prev => (prev + 1) % 5);
                else if (e.code === 'Space') {
                    e.preventDefault();
                    setBongoConfig(prev => ({ ...prev, visible: !prev.visible }));
                }
            }
            // Atajos Contextuales de Dance Cam
            else if (danceConfig.visible) {
                if (e.key === '0') setFiestaMode(prev => !prev);
                else if (e.key === '1') setExplodeTrigger(prev => prev + 1);
                else if (e.code === 'Space') {
                    e.preventDefault();
                    setDanceConfig(prev => ({ ...prev, visible: !prev.visible }));
                }
            }
            // Atajos Contextuales de Marcador Asistencia
            else if (config.visible) {
                if (e.key === '1') {
                    setConfig(prev => ({ ...prev, visible: false }));
                    setTimeout(() => setConfig(prev => ({ ...prev, visible: true })), 50);
                } else if (e.key === '2') {
                    const flareEl = document.querySelector('.soccer-lens-flare');
                    if (flareEl) {
                        flareEl.classList.remove('ignite-flare');
                        void flareEl.offsetWidth;
                        flareEl.classList.add('ignite-flare');
                    }
                } else if (e.key === '3') {
                    setConfig(prev => ({ ...prev, visible: false }));
                } else if (e.code === 'Space') {
                    e.preventDefault();
                    setConfig(prev => ({ ...prev, visible: !prev.visible }));
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [kissConfig.visible, bongoConfig.enabled, bongoConfig.visible, danceConfig.visible, config.visible, intensityLevel]);

    return (
        <>
            {config.visible && <SoccerOverlay config={config} />}

            {danceConfig.visible && (
                <DanceOverlay 
                    config={danceConfig} 
                    activeDancers={activeDancers} 
                    fiestaMode={fiestaMode}
                    manualExplodeTrigger={explodeTrigger}
                />
            )}

            {bongoConfig.enabled && bongoConfig.visible && (
                <BongoOverlay 
                    config={bongoConfig}
                    leftHitActive={leftHit}
                    rightHitActive={rightHit}
                    intensityLevel={intensityLevel}
                    shaking={shaking}
                    comboCount={comboCount}
                />
            )}

            {kissConfig.visible && (
                <KissOverlay 
                    config={kissConfig}
                    tensionActive={tensionActive}
                    heartTensionActive={heartTensionActive}
                    successActive={successActive}
                    failActive={failActive}
                    destelloActive={destelloActive}
                />
            )}

            {isLineupOverlayVisible && <LineupOverlay data={lineupData} />}
            <ScoreOverlay data={scoreData} />
            <CardsOverlay data={cardsData} />

            {/* Estilos stroboscópicos compartidos de destello */}
            <style>{`
                .soccer-lens-flare.ignite-flare {
                    animation: flare-burst 1.5s cubic-bezier(0.1, 0.8, 0.15, 1) forwards !important;
                }
            `}</style>

            {connectionStatus === 'error' && (
                <div style={{ position: 'fixed', bottom: 0, right: 0, background: '#ef4444', color: 'white', padding: '4px 8px', fontSize: 10, zIndex: 99999 }}>
                    FOOTBALL WS DISCONNECTED
                </div>
            )}
        </>
    );
};

export { Soccer };
export default Soccer;
